import React, { useEffect } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels"; // Importa il plugin per le etichette dei dati
import { getLabelFromPatientStatus, titleCase } from "../utils/common";
import { labels, psTranslate } from "../../src/pages/shared/translations";
import moment from "moment";

// Registra i componenti di Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels
);
import { Divider, Grid, Typography } from "@material-ui/core";
import { SpTable, SpTableCell, SpTableRow } from "../components/bundles";
import SpText from "../components/atoms/SpText";

const ReportGenerator = ({
  reportData,
  handleClick,
  setHandleClick,
  setLoading,
  logoImg,
  start_date,
  end_date,
}) => {
  useEffect(() => {
    if (handleClick) {
      generatePDF();
      setHandleClick(false);
    }
  }, [handleClick]);

  const generatePDF = () => {
    setLoading(true);

    const doc = new jsPDF("landscape");
    doc.setFontSize(18);

    const addHeader = (doc) => {
      const pageWidth = doc.internal.pageSize.getWidth() - 50;

      // Barra colorata
      const barHeight = 1; // Altezza della barra
      const barWidth = pageWidth; // Larghezza della barra
      doc.setDrawColor(73, 197, 182); // Colore RGB della barra
      doc.setFillColor(73, 197, 182);
      doc.rect(0, 201, barWidth, barHeight, "F"); // Disegna la barra (F = fill)

      // Testo sotto la barra
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(73, 197, 182);
      const textFooter = `${moment(start_date).format("DD/MM/YYYY")} - ${moment(
        end_date
      ).format("DD/MM/YYYY")}`;
      const textWidth =
        (doc.getStringUnitWidth(textFooter) * doc.internal.getFontSize()) /
        doc.internal.scaleFactor;
      const textX = (doc.internal.pageSize.getWidth() - textWidth) / 2; // Centra il testo orizzontalmente
      doc.text(textFooter, textX, 207);
    };

    const addImage = () => {
      const imgWidth = 19;
      const imgHeight = (logoImg.height / logoImg.width) * imgWidth;
      doc.addImage(logoImg, "PNG", 260, 200, imgWidth, imgHeight);
      addHeader(doc);
    };

    doc.setFontSize(12);

    const imgWidth = 190;
    const imgHeight = (logoImg.height / logoImg.width) * imgWidth;
    doc.addImage(logoImg, "PNG", 50, 90, imgWidth, imgHeight);

    doc.addPage();
    addImage();
    const stats = [
      [labels.analytics.account.patNumber, reportData.patientNumber],
      [labels.analytics.account.groupNumber, reportData.groupNumber],
      [labels.analytics.account.actNumber, reportData.activityNumber],
      [
        labels.analytics.account.presTypeFreq,
        reportData.mostFrequentPresentationType,
      ],
      [labels.analytics.account.presLonger, reportData.longerPresentationDays],
      [labels.analytics.account.areaAffected, reportData.mostAffectedArea],
      [
        labels.analytics.account.structureAffected,
        reportData.mostAffectedStructure,
      ],
    ];
    doc.autoTable({
      startY: 25,
      head: [["Descrizione", "Valore"]],
      body: stats,
    });

    doc.addPage();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(labels.analytics.account.summaryPresentation, 10, 10);
    addImage();
    const injuriesTable = reportData.presentationWithRaceCounts.map((item) => {
      const labelInjury = `${psTranslate(
        item?.["pathology.name"]
      )} ${psTranslate(item?.["region.name"])}`;
      return [
        titleCase(item["patient.givenName"]),
        titleCase(item["patient.familyName"]),
        labelInjury,
        moment(item.start_date).format("DD/MM/YYYY"),
        moment(item?.end_date).format("DD/MM/YYYY"),
        item?.race_count,
      ];
    });

    doc.autoTable({
      startY: 25,
      head: [
        [
          labels.account.accountForm.givenName,
          labels.account.accountForm.familyName,
          labels.patient.presentation.tableReason,
          labels.analytics.account.startDate,
          labels.analytics.account.endDate,
          labels.account.accountReports.competitionsLost,
        ],
      ],
      body: injuriesTable,
    });

    // Grafico delle presentazioni per regione
    const presentationChart = document.getElementById("presentationChart");
    if (presentationChart) {
      const chartImage = presentationChart.toDataURL("image/png");
      doc.addPage();
      addImage();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(labels.account.accountReports.injuryRegion, 10, 10);
      doc.text(labels.account.accountReports.injuryDetail, 10, 20);
      doc.addImage(chartImage, "PNG", 15, 30, 260, 90);
    }

    // Grafico delle attività
    const activityChart = document.getElementById("activityChart");
    if (activityChart) {
      doc.addPage();
      addImage();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      const activityImage = activityChart.toDataURL("image/png");
      doc.text(labels.account.accountReports.minActivity, 10, 10);
      doc.text(labels.account.accountReports.mainActivityDetail, 10, 20);
      doc.addImage(activityImage, "PNG", 15, 30, 260, 90);
    }

    doc.addPage();
    addImage();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(labels.account.accountReports.athletes, 100, 100);
    doc.text(labels.account.accountReports.athletesDetails, 100, 110);
    // Grafico delle presentazioni per atleta
    reportData.availability.map((athlete, index) => {
      const patientChart = document.getElementById(`patientChart${index}`);
      const patientChartTreatment = document.getElementById(
        `patientChartTreatment${index}`
      );
      if (patientChart || patientChartTreatment) {
        const chartImage = patientChart.toDataURL("image/png");
        const chartImageTreatment =
          patientChartTreatment.toDataURL("image/png");
        doc.addPage();
        addImage();
        doc.addImage(chartImage, "PNG", 50, 10, 180, 90);
        doc.addImage(chartImageTreatment, "PNG", 50, 105, 180, 90);
      }
    });

    // Grafico delle attività
    const activityDuration = document.getElementById("activityDuration");
    if (activityDuration) {
      doc.addPage();
      addImage();
      const activityImage = activityDuration.toDataURL("image/png");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(labels.account.accountReports.activityDuration, 10, 10);
      doc.text(labels.account.accountReports.activityDurationDetail, 10, 20);
      doc.addImage(activityImage, "PNG", 10, 30, 280, 90);
    }

    // Salvare il PDF
    setLoading(false);
    doc.save(`report${moment().format("DD/MM/YYYY")}.pdf`);
  };

  // Dati per il grafico delle presentazioni per regione
  const regionLabels = reportData.presentationRows.map((row) =>
    psTranslate(row.name)
  );
  const regionCounts = reportData.presentationRows.map(
    (row) => row.regionCount
  );

  const presentationData = {
    labels: regionLabels,
    datasets: [
      {
        label: labels.account.accountReports.injuryRegion,
        data: regionCounts,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        datalabels: {
          color: "white", // Colore del testo dei numeri
          font: {
            weight: "bold",
            size: 12,
          },
          formatter: (value, context) => {
            // Prendi il nome dell'attività
            const activityName = context.chart.data.labels[context.dataIndex];
            // Trova l'attività corrispondente nel reportData.presentationRows
            const activityRow = reportData.presentationRows.find(
              (row) => row.name === activityName
            );
            // Se trovi l'attività, prendi il totalDuration (in minuti) e aggiungi l'etichetta
            const totalDuration = activityRow ? activityRow.daysPassed : 0;

            // Restituisci la formattazione desiderata
            return `${value} ${labels.groups.federationAdd.injuryList.title}`;
          },
        },
      },
    ],
  };

  // Funzione per raggruppare i dati di stato di salute per atleta
  const processHealthData = (calendarStatus) => {
    const grouped = calendarStatus.reduce((acc, entry) => {
      acc[entry.key] = (acc[entry.key] || 0) + 1;
      return acc;
    }, {});
    return grouped;
  };

  const processSubactivities = (data) => {
    return data.reduce((acc, item) => {
      const { name, duration } = item;
      const durationNumber = parseFloat(duration); // Converte la durata in numero

      // Aggiungi la durata al totale dell'attività
      if (acc[name]) {
        acc[name] += durationNumber;
      } else {
        acc[name] = durationNumber;
      }

      return acc;
    }, {});
  };

  // Dati per il grafico delle attività
  const activityLabels = reportData.activitiesRows.map(
    (row) => psTranslate(row.name) || "Non specificato"
  );
  const activityCounts = reportData.activitiesRows.map(
    (row) => row.activityCount
  );

  const activityData = {
    labels: activityLabels,
    datasets: [
      {
        label: labels.account.accountReports.minActivity,
        data: activityCounts,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        datalabels: {
          color: "white", // Colore del testo dei numeri
          font: {
            weight: "bold",
            size: 12,
          },
          formatter: (value, context) => {
            // Prendi il nome dell'attività
            const activityName = context.chart.data.labels[context.dataIndex];

            // Trova l'attività corrispondente nel reportData.activitiesRows
            const activityRow = reportData.activitiesRows.find(
              (row) => row.name === activityName
            );

            // Se trovi l'attività, prendi il totalDuration (in minuti) e aggiungi l'etichetta
            const totalDuration = activityRow ? activityRow.totalDuration : 0;

            // Calcolare i minuti
            const hours = (totalDuration / 60).toFixed(1); // Converti i minuti in ore

            // Restituisci la formattazione desiderata
            return `${value} att.\n${hours} h`;
          },
        },
      },
    ],
  };

  // Colori per ogni tipo di attività
  const activityColors = {
    assessment: "rgba(75, 192, 192, 0.6)",
    treatment: "rgba(255, 99, 132, 0.6)",
    exercise: "rgba(54, 162, 235, 0.6)",
    training: "rgba(255, 206, 86, 0.6)",
    rest: "rgba(153, 102, 255, 0.6)",
    appointment: "rgba(255, 159, 64, 0.6)",
    race: "rgba(100, 100, 100, 0.6)",
    rehabilitation: "rgba(200, 99, 132, 0.6)",
    supplement: "rgba(75, 192, 192, 0.3)",
  };

  // Raggruppa i dati per creare dataset del grafico
  const generateChartData = () => {
    const athleteNames = reportData.availability.map(
      (athlete, index) =>
        titleCase(
          `${athlete?.patient?.givenName} ${athlete?.patient?.familyName}`
        ) || `Atleta ${index + 1}`
    );

    const activityTypes = Object.keys(activityColors);

    // Creare un dataset per ogni attività
    const datasets = activityTypes.map((activityType) => {
      const darkenedColor = "rgb(255, 255, 255)";

      return {
        label: activityType,
        data: reportData.availability.map((athlete) => {
          const activity = athlete.monitoring_table.find(
            (a) => a.name === activityType
          );
          return activity ? activity.total_duration : 0;
        }),
        backgroundColor: activityColors[activityType],
        datalabels: {
          color: darkenedColor,
          font: {
            weight: "bold",
            size: 12,
          },
          formatter: (value, context) => value,
        },
      };
    });

    return {
      labels: athleteNames,
      datasets,
    };
  };

  const chartData = generateChartData();

  const options = {
    indexAxis: "y", // Inverti l'asse da verticale a orizzontale
    plugins: {
      legend: { position: "top" },
      tooltip: { mode: "index", intersect: false },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: labels.account.accountReports.activityDuration,
        },
      },
      y: { stacked: true },
    },
  };

  const defaulltOptions = {
    indexAxis: "x", // o "y" se hai un grafico orizzontale
    responsive: true,
    elements: {
      bar: {
        maxBarThickness: 4, // Imposta la larghezza massima delle barre
      },
    },
    scales: {
      xAxes: [
        {
          maxBarThickness: 10,
        },
      ],
    },
  };

  const groupedAthletes = reportData.availability.reduce(
    (result, athlete, index) => {
      const groupIndex = Math.floor(index / 2); // Raggruppa per coppie di 2
      if (!result[groupIndex]) result[groupIndex] = [];
      result[groupIndex].push(athlete);
      return result;
    },
    []
  );

  const headCellsPresentation = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: `${labels.patient.table.header.name} ${labels.patient.table.header.surname}`,
      isAction: false,
    },
    {
      id: "reason",
      numeric: false,
      disablePadding: false,
      label: labels.patient.presentation.tableReason,
      isAction: false,
    },

    {
      id: "start",
      numeric: false,
      disablePadding: false,
      label: labels.patient.presentation.tableStart,
      isAction: false,
    },
    {
      id: "end",
      numeric: false,
      disablePadding: false,
      label: labels.patient.presentation.tableEnd,
      isAction: false,
    },
    {
      id: "duration",
      numeric: false,
      disablePadding: false,
      label: labels.agency.licenses.buyLicenseDialog.duration,
      isAction: false,
    },
    {
      id: "race_count",
      numeric: false,
      disablePadding: false,
      label: labels.account.accountReports.competitionsLost,
      isAction: false,
    },
  ];

  //rendering the profile editing form
  const PresentationRow = ({ row }) => {
    let bodyPartName = "";
    if (row?.["region.name"]) {
      bodyPartName = `${psTranslate(row?.["region.name"])}`;
    }

    return (
      <>
        <SpTableRow tabIndex={-1}>
          <SpTableCell>
            <SpText variant="tableText">
              {`${titleCase(row["patient.givenName"])} ${titleCase(
                row["patient.familyName"]
              )}`}
            </SpText>
          </SpTableCell>
          <SpTableCell>
            <SpText variant="tableText">
              {row?.["pathology.name"]
                ? psTranslate(row?.["pathology.name"]) +
                  " " +
                  bodyPartName?.toLowerCase()
                : psTranslate(row?.type) + " " + bodyPartName?.toLowerCase()}
            </SpText>
          </SpTableCell>

          <SpTableCell>
            <SpText variant="tableText">
              {row.start_date
                ? moment(row.start_date).format("DD/MM/yyyy")
                : ""}
            </SpText>
          </SpTableCell>
          <SpTableCell>
            <SpText variant="tableText">
              {row.end_date && moment(row.end_date).format("DD/MM/yyyy")}
            </SpText>
          </SpTableCell>
          <SpTableCell>
            <SpText
              variant="tableText"
              style={{
                display: "flex",
                "justify-content": "center",
                "align-items": "center",
              }}
            >
              {row?.end_date
                ? parseInt(
                    moment
                      .duration(
                        moment(row.end_date).diff(moment(row.start_date))
                      )
                      .asDays() - 1
                  )
                : parseInt(
                    moment
                      .duration(moment(row).diff(moment(row.start_date)))
                      .asDays() - 1
                  )}
            </SpText>
          </SpTableCell>
          <SpTableCell>
            <SpText
              variant="tableText"
              style={{
                display: "flex",
                "justify-content": "center",
                "align-items": "center",
              }}
            >
              {row?.race_count}
            </SpText>
          </SpTableCell>
        </SpTableRow>
      </>
    );
  };

  return (
    <div>
      <div>
        <h2>{labels.account.accountReports.minActivity}</h2>
        <Typography>
          {labels.account.accountReports.mainActivityDetail}
        </Typography>
        <div style={{ margin: "0 auto" }}>
          <Bar
            data={activityData}
            options={defaulltOptions}
            id="activityChart"
            barSize={40}
          />
        </div>
        <h2>{labels.account.accountReports.injuryRegion}</h2>
        <Typography>{labels.account.accountReports.injuryDetail}</Typography>
        <div style={{ margin: "0 auto" }}>
          <Bar
            data={presentationData}
            options={defaulltOptions}
            id="presentationChart"
            barSize={100}
          />
        </div>
      </div>
      <h2>{labels.account.accountReports.athletes}</h2>
      <Typography>{labels.analytics.account.summaryPresentation}</Typography>

      <SpTable
        id={"injuryTable"}
        headCells={headCellsPresentation}
        rows={reportData.presentationWithRaceCounts}
        pagination={true}
        rowKey="id"
        padding={false}
        notCheckable={false}
        tableContainerMaxHeight={"calc(100vh - 320px)"}
      >
        <PresentationRow />
      </SpTable>
      <Typography>{labels.account.accountReports.athletesDetails}</Typography>

      <div>
        <Grid container spacing={3}>
          {groupedAthletes.map((group, groupIndex) => (
            <Grid container item xs={12} spacing={3} key={groupIndex}>
              {group.map((athlete, index) => {
                const healthData = processHealthData(athlete.calendarStatus);
                const treatmentData = processSubactivities(
                  athlete.monitoring_table.find(
                    ({ key }) => key == "treatmentSubactivities"
                  )?.data
                );
                const labelsTextHealth = Object.keys(healthData).map((item) =>
                  getLabelFromPatientStatus({ status: item })
                );
                const labelsTextTreatment = Object.keys(treatmentData).map(
                  (item) => psTranslate(item)
                );

                const datahealth = Object.values(healthData);
                const dataTreatment = Object.values(treatmentData);

                const chartData = {
                  labels: labelsTextHealth,
                  datasets: [
                    {
                      label: titleCase(
                        `${athlete?.patient?.givenName} ${athlete?.patient?.familyName} [days]`
                      ),
                      data: datahealth,
                      backgroundColor: ["rgba(75, 192, 192, 0.6)"],
                      datalabels: {
                        color: "white", // Colore del testo dei numeri
                        font: {
                          weight: "bold",
                          size: 12,
                        },
                        formatter: (value) => {
                          return `${value}`;
                        },
                      },
                    },
                  ],
                };
                const chartDataTreatment = {
                  labels: labelsTextTreatment,
                  datasets: [
                    {
                      label: titleCase(
                        `${labels.account.accountReports.treatmentsOf} ${athlete?.patient?.givenName} ${athlete?.patient?.familyName} [minutes]`
                      ),
                      data: dataTreatment,
                      backgroundColor: ["rgba(75, 192, 192, 0.6)"],
                      datalabels: {
                        color: "white", // Colore del testo dei numeri
                        font: {
                          weight: "bold",
                          size: 12,
                        },
                        formatter: (value) => {
                          return `${value}`;
                        },
                      },
                    },
                  ],
                };

                return (
                  <Grid item xs={12} sm={6} key={index}>
                    <h3>
                      {`${athlete?.patient?.givenName} ${athlete?.patient?.familyName}` ||
                        `Atleta ${groupIndex * 2 + index + 1}`}
                    </h3>
                    <Bar
                      data={chartData}
                      options={defaulltOptions}
                      id={`patientChart${groupIndex * 2 + index}`}
                    />
                    <Bar
                      data={chartDataTreatment}
                      options={defaulltOptions}
                      id={`patientChartTreatment${groupIndex * 2 + index}`}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ))}
        </Grid>
      </div>
      <h2>{labels.account.accountReports.activityDuration}</h2>
      <Typography>
        {labels.account.accountReports.activityDurationDetail}
      </Typography>

      <Bar data={chartData} options={options} id="activityDuration" />
    </div>
  );
};

export default ReportGenerator;
